import { useNavigate } from "react-router";

import { AuthWrapper } from "@/lib/aws-amplify";
import { isAuthError } from "@/lib/aws-amplify/auth";
import type { CognitoForgotPasswordRequestMetadata } from "@/lib/gql/__generated__/graphql";

import { QUERY_PARAMS } from "@/routes/_constants/query-params";
import { PATHS } from "@/routes/paths";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info/_hooks/use-toast";

/**
 * Forgot Password 時にユーザーから入力を受け取るパラメーター
 */
export type ForgotPasswordParams = {
	email: string;
};

/**
 * Forgot Password 時に副作用として発生するパラメーター
 * @param email
 * @returns
 */
const getCallbackUrlAndCodeParameter = (
	email: string,
): Pick<CognitoForgotPasswordRequestMetadata, "callbackUrl" | "codeParameter"> => {
	const origin = window.location.origin;
	const codeParameter = "{####}";
	const callbackUrl = `${origin}${PATHS.AUTH.RESET_PASSWORD}?${QUERY_PARAMS.AUTH.RESET_PASSWORD_VERIFY.USERNAME}=${encodeURIComponent(email)}&${QUERY_PARAMS.AUTH.RESET_PASSWORD_VERIFY.VERIFY_CODE}=${codeParameter}`;
	return {
		callbackUrl,
		codeParameter,
	};
};

export const useForgotPassword = () => {
	const navigate = useNavigate();

	const forgotPassword = async ({ email }: ForgotPasswordParams) => {
		const { callbackUrl, codeParameter } = getCallbackUrlAndCodeParameter(email);
		const clientMetadata: CognitoForgotPasswordRequestMetadata = {
			callbackUrl,
			codeParameter,
		};
		return AuthWrapper.authResetPassword(email, clientMetadata)
			.then(({ nextStep: { resetPasswordStep } }) => {
				/**
				 * @see file://./../_md/next-step/reset-password-step.md
				 */
				if (resetPasswordStep === "CONFIRM_RESET_PASSWORD_WITH_CODE") {
					notifyUserEventInfo({
						variant: "default",
						title: "認証メールを送信いたしました",
						description: "メールをご確認ください。",
					});
				} else if (resetPasswordStep === "DONE") {
					/**
					 * @description
					 * reset-password のフェーズでは以下の step が送られることはあり得ない。
					 * - DONE
					 */
					notifyUserEventInfo({
						variant: "default",
						title: "パスワードを再設定しました",
						description: "続けてサインインをしてください。",
					});
					navigate(PATHS.AUTH.SIGN_IN);
				}
			})
			.catch((err) => {
				const result = isAuthError(err);
				if (result) {
					/**
					 *
					 * @description
					 * amplify/auth から throw されるエラーは以下、
					 * 1. ForgotPasswordException
					 * 2. AuthValidationErrorCode @see {@link(https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_ConfirmSignUp.html)}
					 * 3. AuthTokenConfigException
					 *
					 * 2,3 はクライアントでハンドリングしているため、1のみ考慮する。
					 */
					const errorType = err?.name;
					if (
						errorType === "TooManyFailedAttemptsException" ||
						errorType === "TooManyRequestsException" ||
						errorType === "LimitExceededException"
					) {
						notifyUserEventInfo({
							variant: "error",
							title: "認証メールの送信に失敗しました",
							description: "時間を空けて、もう一度お試しください。",
						});
					} else if (
						errorType === "InvalidParameterException" &&
						typeof err?.message === "string" &&
						err.message.includes("no registered/verified email or phone_number")
					) {
						// EMAIL_OTP MFA（2段階認証）を有効化したユーザーは、Cognito 仕様により
						// email を recovery channel として再利用できず ForgotPassword が reject される。
						// 同じ InvalidParameterException でも他の原因（パラメータ不正など）と区別するため、
						// Cognito が固定で返すメッセージ文字列で絞り込む。
						notifyUserEventInfo({
							variant: "error",
							title: "パスワードの再設定に失敗しました",
							description:
								"2段階認証を有効にしているアカウントでは、パスワードの再設定をご利用いただけません。2段階認証を無効にしてからご利用ください。ログインできない場合はカスタマーサポートへお問い合わせください。",
						});
					} else {
						notifyUserEventInfo({
							variant: "error",
							title: "認証メールの送信に失敗しました",
							description: "カスタマーサポートへお問い合わせください。",
						});
					}
				} else {
					notifyUserEventInfo({
						variant: "error",
						title: "認証メールの送信に失敗しました",
						description: "もう一度お試しください。",
					});
				}
			});
	};

	return {
		forgotPassword,
	};
};
